<script lang="ts">
	import { onMount, tick } from 'svelte';
	import { page } from '$app/stores';

	import {
		REGISTRATION_INTRO_DEFAULT_TIMEOUT_MS,
		REGISTRATION_INTRO_FADE_MS,
		isRegistrationIntroOnboardingPath,
		selectRegistrationIntroAsset,
		type RegistrationIntroTrigger
	} from './registrationIntroPolicy';
	import {
		completeRegistrationIntro,
		getRegistrationIntroTrigger,
		holdRegistrationIntroCover,
		releaseRegistrationIntroCover
	} from './registrationIntroRuntime';
	import { readRegistrationIntroOnboardingState } from './registrationIntroOnboardingState';

	export let user: { id?: string; token?: string } | null = null;

	let active = false;
	let covering = false;
	let exiting = false;
	let asset = '';
	let trigger: RegistrationIntroTrigger = 'none';
	let video: HTMLVideoElement | null = null;
	let fallbackTimer: ReturnType<typeof setTimeout> | null = null;
	let fadeTimer: ReturnType<typeof setTimeout> | null = null;
	let mounted = false;
	let previewStartedThisDocument = false;
	let onboardingStartedThisDocument = false;
	let previousContext = '';
	let resolvingOnboardingState = false;

	const resolveAuthToken = () =>
		user?.token ??
		(typeof localStorage !== 'undefined' ? localStorage.getItem('token') ?? '' : '');

	const clearTimer = (timer: ReturnType<typeof setTimeout> | null) => {
		if (timer) clearTimeout(timer);
	};

	const armFallback = (timeoutMs = REGISTRATION_INTRO_DEFAULT_TIMEOUT_MS) => {
		clearTimer(fallbackTimer);
		fallbackTimer = setTimeout(finish, timeoutMs);
	};

	const handlePlaying = () => {
		// Once playback begins, the media owns its full timeline. Only native `ended`
		// or `error` may close it; the loading watchdog must never trim the supplied video.
		clearTimer(fallbackTimer);
		fallbackTimer = null;
	};

	const handleWaiting = () => {
		if (active) armFallback();
	};

	const prepareAsset = () => {
		asset = selectRegistrationIntroAsset({
			viewportWidth: window.innerWidth,
			viewportHeight: window.innerHeight
		});
	};

	const finish = () => {
		if (!active || exiting) return;
		exiting = true;
		clearTimer(fallbackTimer);
		const completedTrigger = trigger;
		fadeTimer = setTimeout(() => {
			covering = false;
			active = false;
			exiting = false;
			trigger = 'none';
			releaseRegistrationIntroCover();
			completeRegistrationIntro(completedTrigger);
		}, REGISTRATION_INTRO_FADE_MS);
	};

	const show = async (nextTrigger: Exclude<RegistrationIntroTrigger, 'none'>) => {
		if (active) return;
		if (nextTrigger === 'preview' && previewStartedThisDocument) return;
		if (nextTrigger === 'onboarding' && onboardingStartedThisDocument) return;

		if (nextTrigger === 'preview') previewStartedThisDocument = true;
		if (nextTrigger === 'onboarding') onboardingStartedThisDocument = true;
		covering = false;
		trigger = nextTrigger;
		if (!asset) prepareAsset();
		exiting = false;
		active = true;
		armFallback();

		await tick();
		void video?.play().catch(finish);
	};

	const start = async () => {
		const token = resolveAuthToken();
		// Session auth may be carried by an HttpOnly cookie, so the session user can
		// be authenticated even when no bearer token is exposed in the user object or
		// localStorage. The onboarding-state request already uses credentials: include.
		const authenticated = Boolean(user?.id);
		const previewTrigger = getRegistrationIntroTrigger({
			authenticated,
			onboardingIncomplete: false
		});
		if (previewTrigger === 'preview') {
			await show(previewTrigger);
			return;
		}

		if (!authenticated || !isRegistrationIntroOnboardingPath($page.url.pathname) || onboardingStartedThisDocument) {
			return;
		}
		if (resolvingOnboardingState) return;

		// Match the authenticated onboarding sequence: hide the document splash and
		// let the browser preload the selected WebM while eligibility is resolved.
		prepareAsset();
		holdRegistrationIntroCover();
		covering = true;
		resolvingOnboardingState = true;
		try {
			const state = await readRegistrationIntroOnboardingState(token);
			if (token !== resolveAuthToken()) return;
			if (!state.incomplete) {
				covering = false;
				releaseRegistrationIntroCover();
				return;
			}

			const onboardingTrigger = getRegistrationIntroTrigger({
				authenticated: true,
				onboardingIncomplete: true
			});
			if (onboardingTrigger === 'onboarding') await show(onboardingTrigger);
		} catch {
			// Fail closed: an unavailable onboarding status must never replay the intro
			// for an account that may already have completed or skipped onboarding.
			covering = false;
			releaseRegistrationIntroCover();
		} finally {
			resolvingOnboardingState = false;
		}
	};

	onMount(() => {
		mounted = true;
		previousContext = `${$page.url.pathname}:${user?.id ?? ''}:${resolveAuthToken() ? '1' : '0'}`;
		void start();

		return () => {
			mounted = false;
			clearTimer(fallbackTimer);
			clearTimer(fadeTimer);
		};
	});

	$: if (
		mounted &&
		`${$page.url.pathname}:${user?.id ?? ''}:${resolveAuthToken() ? '1' : '0'}` !== previousContext
	) {
		previousContext = `${$page.url.pathname}:${user?.id ?? ''}:${resolveAuthToken() ? '1' : '0'}`;
		void start();
	}
</script>

{#if active || covering}
	<div
		class:exiting
		class="registration-intro"
		data-aeoll-registration-intro={trigger}
		style={`--registration-intro-fade-ms: ${REGISTRATION_INTRO_FADE_MS}ms`}
		aria-hidden="true"
	>
		{#if asset}
			<video
				bind:this={video}
				class:visible={active}
				src={asset}
				muted
				playsinline
				preload="auto"
				on:playing={handlePlaying}
				on:waiting={handleWaiting}
				on:ended={finish}
				on:error={finish}
			></video>
		{/if}
	</div>
{/if}

<style>
	.registration-intro {
		position: fixed;
		inset: 0;
		z-index: 2147483000;
		width: 100dvw;
		height: 100dvh;
		overflow: hidden;
		background: #000;
		opacity: 1;
		transition: opacity var(--registration-intro-fade-ms) ease-out;
	}

	.registration-intro.exiting {
		opacity: 0;
		pointer-events: none;
	}

	video {
		display: block;
		width: 100%;
		height: 100%;
		object-fit: cover;
		object-position: center;
		background: #000;
		opacity: 0;
	}

	video.visible {
		opacity: 1;
	}
</style>
